import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { assetService } from '../../../services/assetService';
import AssetEditModal from './AssetEditModal';

const AssetManagementPanel = ({ showUploadModal, onUploadClick, onCloseUploadModal }) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [editingAsset, setEditingAsset] = useState(null);
  const [deletingAssetId, setDeletingAssetId] = useState(null);

  const categories = [
    { value: 'all', label: 'All Assets', icon: 'FolderOpen' },
    { value: 'logo', label: 'Logos', icon: 'Image' },
    { value: 'technology', label: 'Technology', icon: 'Cpu' },
    { value: 'photo', label: 'Photos', icon: 'Camera' },
    { value: 'diagram', label: 'Diagrams', icon: 'FileText' },
    { value: 'video', label: 'Videos', icon: 'Video' }
  ];

  useEffect(() => {
    loadAssets();
  }, [selectedCategory]);

  // Reload assets when upload modal closes (indicating successful upload)
  useEffect(() => {
    if (!showUploadModal) {
      loadAssets();
    }
  }, [showUploadModal]);

  const loadAssets = async () => {
    try {
      setLoading(true);
      const data = await assetService?.getAll({ category: selectedCategory });
      setAssets(data);
      setError('');
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingAssetId(assetId);
      await assetService?.delete(assetId);
      setAssets(assets?.filter(a => a?.id !== assetId));
    } catch (err) {
      alert('Failed to delete asset: ' + err?.message);
    } finally {
      setDeletingAssetId(null);
    }
  };

  const handleEditSuccess = () => {
    setEditingAsset(null);
    loadAssets();
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Asset Management</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Upload, edit, replace, and delete marketing assets
            </p>
          </div>
          <Button
            onClick={onUploadClick}
            iconName="Upload"
            iconPosition="left"
          >
            Upload Asset
          </Button>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2">
          {categories?.map((cat) => (
            <button
              key={cat?.value}
              onClick={() => setSelectedCategory(cat?.value)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                selectedCategory === cat?.value
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-foreground border-border hover:bg-muted'
              }`}
            >
              <Icon name={cat?.icon} size={16} />
              <span className="text-sm font-medium">{cat?.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Icon name="Loader" size={32} className="animate-spin text-primary mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Loading assets...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Icon name="AlertCircle" size={32} className="text-destructive mx-auto mb-2" />
              <p className="text-sm text-destructive">{error}</p>
              <Button onClick={loadAssets} variant="outline" size="sm" className="mt-4">
                Retry
              </Button>
            </div>
          </div>
        ) : assets?.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Icon name="FolderOpen" size={48} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-foreground font-medium mb-1">No assets found</p>
              <p className="text-sm text-muted-foreground mb-4">
                Upload your first asset to get started
              </p>
              <Button onClick={onUploadClick} size="sm">
                Upload Asset
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assets?.map((asset) => (
              <div
                key={asset?.id}
                className="bg-background border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="relative aspect-video bg-muted">
                  {asset?.fileType === 'video' ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Icon name="Video" size={48} className="text-muted-foreground" />
                    </div>
                  ) : (
                    <img
                      src={asset?.thumbnail}
                      alt={asset?.title}
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute top-2 right-2 bg-card rounded px-2 py-1 text-xs font-medium">
                    {asset?.format}
                  </div>
                </div>

                <div className="p-4">
                  <h3 className="font-semibold text-foreground mb-1 line-clamp-1">
                    {asset?.title}
                  </h3>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                    <span>{asset?.dimensions}</span>
                    <span>•</span>
                    <span>{asset?.fileSize}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingAsset(asset)}
                      iconName="Edit"
                      className="flex-1"
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(asset?.id)}
                      loading={deletingAssetId === asset?.id}
                      disabled={deletingAssetId === asset?.id}
                      iconName="Trash2"
                      className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Edit Modal */}
      {editingAsset && (
        <AssetEditModal
          asset={editingAsset}
          onClose={() => setEditingAsset(null)}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};

export default AssetManagementPanel;